import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Section, ErrorPanel, LoadingRow, UserLink, EmptyNote, MarketTag } from '../components/ui/bits';
import { Badge } from '../components/ui/Badge';
import { MrrTiles } from '../components/MrrTiles';
import { useRpc } from '../lib/queries';
import { agoLabel, change } from '../lib/format';
import { SEV_META, type InboxItem } from '../lib/classes';
import { sum, split } from '../lib/market';
import { selfLabel, channelLabel, planLabel, stageLabel } from '../lib/labels';
import type { AnalyticsAlert, Overview } from '../lib/types';

const SEV_RANK: Record<string, number> = { crit: 0, warn: 1, info: 2 };

/** General: the one-screen read of today. Every number excludes internal accounts. */
export const OverviewPage: React.FC = () => {
  const { data: p, error, isLoading } = useRpc<Overview>('admin_dash_overview', {}, { refetchInterval: 120_000 });
  const inbox = useRpc<InboxItem[]>('admin_inbox');
  const alerts = useRpc<AnalyticsAlert[]>('admin_analytics_alerts', {}, { refetchInterval: 300_000 });

  if (isLoading) return <LoadingRow label="Loading the overview" />;
  if (error || !p) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_overview" />;

  const urgent = (inbox.data ?? []).filter(i => i.severity !== 'info');
  const emails = p.emails_today.lifecycle + p.emails_today.alerts + p.emails_today.welcome;
  const subs = split(p.payers.subscribers);

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle={<>Today in London, internal accounts excluded. Read {agoLabel(p.generated_at)}.</>}
      />

      <section aria-label="Tracking alerts" className="mb-4 rounded-lg border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-200">
          <h2 className="text-[13.5px] font-semibold text-stone-900">Alerts</h2>
          <span className="text-[11.5px] text-stone-500">Checked every 5 minutes. They live here only; nothing is emailed. Source: admin_analytics_alerts</span>
        </div>
        {alerts.error ? (
          <div className="p-3"><ErrorPanel error={alerts.error} label="admin_analytics_alerts" /></div>
        ) : alerts.isLoading ? (
          <p className="px-4 py-3 text-[13px] text-stone-500">Checking</p>
        ) : (alerts.data ?? []).length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> No alerts</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {[...(alerts.data ?? [])].sort((x, y) => SEV_RANK[x.severity] - SEV_RANK[y.severity]).map(a => (
              <div key={a.code} className={`flex items-start gap-3 px-4 py-2.5 ${a.severity === 'crit' ? 'bg-rose-50' : ''}`}>
                <Badge tone={a.severity === 'crit' ? 'rose' : a.severity === 'warn' ? 'amber' : 'stone'}>{a.severity === 'crit' ? 'Critical' : a.severity === 'warn' ? 'Warning' : 'Note'}</Badge>
                <span className={`text-[13px] ${a.severity === 'crit' ? 'text-rose-800 font-medium' : a.severity === 'warn' ? 'text-stone-800' : 'text-stone-500'}`}>{a.message}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
        <StatTile label="Signups today" value={sum(p.signups.today)} split={p.signups.today}
          sub={`${sum(p.signups.yesterday)} yesterday`} source="auth.users, since London midnight" />
        <StatTile label="Signups, 7 days" value={sum(p.signups.d7)} split={p.signups.d7}
          delta={change(sum(p.signups.d7), sum(p.signups.prev7))} sub={`${sum(p.signups.prev7)} the 7 days before`} source="auth.users" />
        <StatTile label="Active users, 7 days" value={sum(p.active.d7)} split={p.active.d7}
          sub={`${sum(p.active.d1)} in 24 hours, ${sum(p.active.d30)} in 30 days`}
          source="Sessions, psychometric runs, Vyo messages, tools and non-passive events" />
        <StatTile label="Paying subscribers" value={sum(subs)} split={subs}
          sub={`${sum(p.payers.past_due)} past due, ${sum(p.payers.cancelling)} cancelling, ${p.payers.comped} comped not counted`}
          source="Stripe-linked subscriptions, paying or cancelling. Market from profile" />
        <MrrTiles />
        <StatTile label="Pack holders" value={sum(p.payers.pack_holders)} split={p.payers.pack_holders}
          sub={`${sum(p.payers.packs_sold_30d)} packs sold in 30 days`} source="firm_passes with a Stripe payment, not refunded, unexpired" />
        <StatTile label="Vyo messages, 7 days" value={sum(p.vyo.d7)} split={p.vyo.d7}
          sub={`${sum(p.vyo.senders_7d)} people, ${sum(p.vyo.today)} today`} source="coach_messages, role user" />
        <StatTile label="Sessions, 7 days" value={p.sessions_7d.uk + p.sessions_7d.us} split={{ uk: p.sessions_7d.uk, us: p.sessions_7d.us }}
          sub={`${p.sessions_7d.failed} failed. Psychometric runs: ${sum(p.psy_runs_7d)}`} source="sessions, psychometric results left out" />
        <StatTile label="Offers logged" value={p.offers.total} sub={`${p.offers.d30} in 30 days`} source="user_outcomes, outcome offer" />
        <StatTile label="Open programmes" value={sum(p.open_programmes)} split={p.open_programmes} source="firm_programmes by open date and deadline" />
        <StatTile label="Emails sent today" value={emails.toLocaleString('en-GB')}
          sub={`${p.emails_today.lifecycle} lifecycle, ${p.emails_today.alerts} alerts, ${p.emails_today.welcome} welcome`}
          source="Since London midnight. No daily cap: Resend paid plan" />
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-3">
        <Link to="/alerts" className="block">
          <div className="rounded-lg border border-stone-200 p-4 h-full hover:border-stone-300 transition-colors">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${p.switches?.firm_alerts_live ? 'bg-emerald-500' : 'bg-stone-300'}`} />
              <span className="text-[14px] font-semibold text-stone-900">Radar {p.switches?.firm_alerts_live ? 'armed' : 'paused'}</span>
            </div>
            <p className="text-[12.5px] text-stone-500">{p.dispatch_pending} queued, {p.held_events} held, {p.dispatch_errors_30d} dispatch errors in 30 days</p>
            <p className="text-[12.5px] text-indigo-600 font-medium mt-2 flex items-center gap-1">Radar <ArrowRight className="w-3.5 h-3.5" /></p>
          </div>
        </Link>
        <Link to="/email" className="block">
          <div className="rounded-lg border border-stone-200 p-4 h-full hover:border-stone-300 transition-colors">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${p.switches?.lifecycle_emails_live ? 'bg-emerald-500' : 'bg-stone-300'}`} />
              <span className="text-[14px] font-semibold text-stone-900">Email</span>
            </div>
            <p className="text-[12.5px] text-stone-500">
              Lifecycle {p.switches?.lifecycle_emails_live ? 'on' : 'off'}, referral rewards {p.switches?.referral_rewards_live ? 'on' : 'off'}, {emails} sent today
            </p>
            <p className="text-[12.5px] text-indigo-600 font-medium mt-2 flex items-center gap-1">Switchboard <ArrowRight className="w-3.5 h-3.5" /></p>
          </div>
        </Link>
        <Link to="/inbox" className="block">
          <div className={`rounded-lg border p-4 h-full transition-colors ${urgent.length ? 'bg-amber-50 border-amber-200' : 'border-stone-200 hover:border-stone-300'}`}>
            <div className="flex items-center gap-2 mb-1">
              {urgent.length === 0 && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              <span className="text-[14px] font-semibold text-stone-900">
                {inbox.error ? 'Inbox could not load' : urgent.length ? `${urgent.length} item${urgent.length > 1 ? 's need' : ' needs'} attention` : 'Nothing urgent'}
              </span>
            </div>
            {urgent.slice(0, 3).map(i => (
              <p key={i.key} className="text-[12.5px] text-stone-600 flex items-center gap-1.5">
                <Badge tone={SEV_META[i.severity].tone}>{SEV_META[i.severity].label}</Badge> {i.title} ({i.n})
              </p>
            ))}
            <p className="text-[12.5px] text-indigo-600 font-medium mt-2 flex items-center gap-1">Inbox <ArrowRight className="w-3.5 h-3.5" /></p>
          </div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        <Section title="Latest signups" sub="Self-reported source first, first-touch channel second." source="profiles">
          {p.latest_signups.length === 0 ? <EmptyNote>No signups</EmptyNote> : (
            <div className="divide-y divide-stone-100">
              {p.latest_signups.map(s => (
                <div key={s.id} className="py-2 flex items-start justify-between gap-2 text-[13px]">
                  <div className="min-w-0">
                    <UserLink id={s.id} label={s.full_name || null} />
                    <p className="text-[12px] text-stone-500 truncate">{selfLabel(s.self_source)} · {channelLabel(s.channel)}{s.university ? ` · ${s.university}` : ''}</p>
                  </div>
                  <span className="flex items-center gap-2 shrink-0 text-[12px] text-stone-400"><MarketTag market={s.market} />{agoLabel(s.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Latest purchases" sub="Subscriptions by first payment, and paid packs." source="processed_stripe_events (first paid invoice), firm_passes with a Stripe payment">
          {p.latest_purchases.length === 0 ? <EmptyNote>No purchases</EmptyNote> : (
            <div className="divide-y divide-stone-100">
              {p.latest_purchases.map((x, i) => (
                <div key={i} className="py-2 flex items-start justify-between gap-2 text-[13px]">
                  <div className="min-w-0">
                    <span className="font-medium text-stone-900">{planLabel(x.plan)}</span>
                    {x.firm_name && <span className="text-stone-500"> · {x.firm_name}</span>}
                    <p className="text-[12px]"><UserLink id={x.user_id} /></p>
                  </div>
                  <span className="flex items-center gap-2 shrink-0 text-[12px] text-stone-400"><MarketTag market={x.market} />{agoLabel(x.at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Latest outcomes" source="user_outcomes">
          {p.latest_outcomes.length === 0 ? <EmptyNote>No outcomes logged</EmptyNote> : (
            <div className="divide-y divide-stone-100">
              {p.latest_outcomes.map((o, i) => (
                <div key={i} className="py-2 flex items-center justify-between gap-2 text-[13px]">
                  <span className="min-w-0 truncate">
                    <Badge tone={o.outcome === 'offer' ? 'emerald' : o.outcome === 'rejection' ? 'rose' : 'stone'}>
                      {o.outcome === 'offer' ? 'Offer' : o.outcome === 'rejection' ? 'Rejected' : 'Withdrew'}
                    </Badge>{' '}
                    <span className="text-stone-900 font-medium">{o.firm_name}</span>
                    <span className="text-stone-500"> · {stageLabel(o.career_stage)}</span>
                  </span>
                  <span className="text-[12px] text-stone-400 shrink-0">{agoLabel(o.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};
